// Helpers for the 40 CFR 1090 Control Strategy settings.
//
// "1090 Parameter" (stored as `tenNinetyParameter` on the Control Strategy config
// doc) lets a user pin which 1090 reference parameter a chart is evaluated as.
// 'Auto' keeps the calc's name/alias matching (BENZENE -> Benzene, TOTAL SULFUR ->
// Sulfur, PTOT/RVP -> the vapour-pressure row, `_PC` stripped); any other value
// overrides the parameter half of the `<parameter>_<material>` reference key in
// functions/calculations/sheetCal/check1090.js.
//
// Users see "RVP". The calc's reference row is still named 'DVPE_Gasoline', and
// configs/reports saved while the option was labelled 'DVPE' are read as RVP.

export const TEN_NINETY_PARAMETER_DEFAULT = 'Auto';

export const TEN_NINETY_PARAMETER_OPTIONS = [
  TEN_NINETY_PARAMETER_DEFAULT,
  'Sulfur',
  'Benzene',
  'Oxygenates',
  'RVP'
];

// Older saved values -> current option.
const LEGACY_PARAMETER_ALIASES = { dvpe: 'RVP' };

// Parameters the 1090 reference table defines for each "1090 Material" dropdown
// value (src/pages/chart.js tenNinetyMaterial). Gasoline has Sulfur, Benzene,
// Oxygenates and RVP; every other material is Sulfur only. Keep one material per
// line: functions/__tests__/check1090ParameterOverride.test.js parses this block
// and checks it against the calc reference table.
export const TEN_NINETY_PARAMETERS_BY_MATERIAL = {
  Gasoline: ['Sulfur', 'Benzene', 'Oxygenates', 'RVP'],
  ULSD: ['Sulfur'],
  '500 ppm LM diesel fuel': ['Sulfur'],
  'ECA Marine fuel': ['Sulfur'],
  Butane: ['Sulfur']
};

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

// The reference-table spellings of the two "... fuel" materials (see check1090.js
// MATERIAL_ALIASES_1090), so either spelling finds the same entry.
const MATERIAL_ALIASES = {
  '500 ppm lm diesel': '500 ppm lm diesel fuel',
  'eca marine': 'eca marine fuel'
};

function allowedParametersFor(material) {
  const base = normalizeText(material);
  const wanted = MATERIAL_ALIASES[base] || base;
  const key = Object.keys(TEN_NINETY_PARAMETERS_BY_MATERIAL).find(
    (m) => normalizeText(m) === wanted
  );
  return key ? TEN_NINETY_PARAMETERS_BY_MATERIAL[key] : [];
}

// Canonical dropdown value for a stored setting: case-insensitive, legacy 'DVPE'
// -> 'RVP', anything blank/unknown -> 'Auto'.
export function normalizeTenNinetyParameter(value) {
  const text = normalizeText(value);
  if (LEGACY_PARAMETER_ALIASES[text]) return LEGACY_PARAMETER_ALIASES[text];
  return (
    TEN_NINETY_PARAMETER_OPTIONS.find((o) => o.toLowerCase() === text) ||
    TEN_NINETY_PARAMETER_DEFAULT
  );
}

// Dropdown options for the selected material. Auto is always offered; N/A or an
// unknown material offers only Auto.
export function getTenNinetyParameterOptions(material) {
  return [TEN_NINETY_PARAMETER_DEFAULT, ...allowedParametersFor(material)];
}

export function isTenNinetyParameterValidForMaterial(parameter, material) {
  const normalized = normalizeTenNinetyParameter(parameter);
  return getTenNinetyParameterOptions(material).includes(normalized);
}

// The value to keep for `parameter` once `material` is selected: the canonical
// parameter when the combo exists, else 'Auto' (e.g. RVP after switching to ULSD).
export function resolveTenNinetyParameterForMaterial(parameter, material) {
  return isTenNinetyParameterValidForMaterial(parameter, material)
    ? normalizeTenNinetyParameter(parameter)
    : TEN_NINETY_PARAMETER_DEFAULT;
}

// The explicit 1090 parameter override the calc will apply for this material, or
// '' when the setting is Auto/blank/unknown or not valid for the material (the calc
// ignores those too and falls back to Auto).
export function getTenNinetyParameterOverride(value, material) {
  const resolved = resolveTenNinetyParameterForMaterial(value, material);
  return resolved === TEN_NINETY_PARAMETER_DEFAULT ? '' : resolved;
}

// Parameter label for the 1090 report: the chart's parameter, plus the effective
// 1090 parameter when the report was generated with an override. The override was
// validated against the material when the report was saved.
export function format1090ReportParameter(report) {
  const parameter = report?.parameter ?? '';
  const override = normalizeTenNinetyParameter(report?.parameter1090);
  return override !== TEN_NINETY_PARAMETER_DEFAULT
    ? `${parameter} (1090 parameter: ${override})`
    : parameter;
}
